import React, { useState } from 'react';
import { NEW_SERVICES } from '../../data/servicesData';
import NewServiceCard from '../../components/NewServiceCard';
import TopBar from '../../components/layout/TopBar';
import Footer from '../../components/layout/Footer';
import { buildConfiguredBookingData, getServiceDisplayPrice } from '../../config/servicePricingConfig';
import QuickBookingModal from '../../components/QuickBookingModal';

const NewServicePage = ({ onNavigate }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('newest');
  const [filterBadge, setFilterBadge] = useState('all');
  const [showQuickBookingModal, setShowQuickBookingModal] = useState(false);
  const [preBookingData, setPreBookingData] = useState(null);

  // Get unique badges
  const badges = ['all', ...new Set(NEW_SERVICES.map(service => service.badge).filter(Boolean))];

  // Filter and sort services
  const filteredServices = NEW_SERVICES.filter(service => {
    const matchesSearch = service.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         service.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesBadge = filterBadge === 'all' || service.badge === filterBadge;
    return matchesSearch && matchesBadge;
  });

  const sortedServices = filteredServices
    .map((service) => ({
      ...service,
      price: getServiceDisplayPrice(service),
      originalPrice: getServiceDisplayPrice(service)
    }))
    .sort((a, b) => {
    switch (sortBy) {
      case 'price-low':
        return a.price - b.price;
      case 'price-high':
        return b.price - a.price;
      case 'rating':
        return b.rating - a.rating;
      case 'newest':
      default:
        return b.id - a.id; // Assuming higher ID means newer
    }
    });

  const handleBookNow = (service) => {
    const bookingData = buildConfiguredBookingData(service, 'new-services');
    setPreBookingData(bookingData);
    setShowQuickBookingModal(true);
  };

  const handlePreBookingContinue = (bookingData) => {
    if (!onNavigate) return;
    onNavigate('booking', { bookingData });
  };

  const handleLearnMore = (service) => {
    if (onNavigate) {
      onNavigate('service-details', {
        service: {
          ...service,
          servicePage: 'new-services'
        }
      });
    }
  };

  const handleSearch = (term) => {
    setSearchTerm(term);
  };

  const formatDisplayName = (badge) => {
    if (badge === 'all') return 'All Services';
    return badge.charAt(0).toUpperCase() + badge.slice(1).toLowerCase();
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* TopBar */}
      <TopBar 
        onUserClick={() => onNavigate && onNavigate('login')}
        onSearch={handleSearch}
        onSignupUser={() => onNavigate && onNavigate('signup')}
        onSignupPandit={() => onNavigate && onNavigate('signup')}
        onLogoClick={() => onNavigate && onNavigate('home')}
      />

      {/* Hero Section */}
      <section className="py-8 sm:py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Breadcrumb */}
          <nav className="mb-6">
            <ol className="flex items-center space-x-2 text-sm text-gray-600">
              <li>
                <button
                  onClick={() => onNavigate && onNavigate('home')}
                  className="hover:text-blue-600 transition-colors"
                >
                  Home
                </button>
              </li>
              <li className="flex items-center">
                <svg className="w-4 h-4 mx-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
                <span className="text-gray-900 font-medium">New Services</span>
              </li>
            </ol>
          </nav>

          {/* Hero Content */}
          <div className="text-center bg-white rounded-2xl p-8 shadow-lg border border-blue-100">
            <div className="text-6xl mb-4">✨</div>
            <h1 className="text-4xl lg:text-5xl font-bold text-blue-800 mb-4">
              New & Noteworthy Services
            </h1>
            <p className="text-lg text-gray-600 mb-6 max-w-3xl mx-auto">
              Discover our latest spiritual services and innovative offerings
            </p>

            {/* Stats */}
            <div className="flex justify-center items-center space-x-8 mb-6">
              <div className="text-center">
                <div className="text-2xl font-bold text-blue-600">{NEW_SERVICES.length}+</div>
                <div className="text-sm text-gray-600">New Services</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-blue-600">{NEW_SERVICES.reduce((sum, service) => sum + (service.reviewCount || 0), 0)}+</div>
                <div className="text-sm text-gray-600">Early Adopters</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-blue-600">{(NEW_SERVICES.reduce((sum, service) => sum + (service.rating || 0), 0) / NEW_SERVICES.length).toFixed(1)}★</div>
                <div className="text-sm text-gray-600">Average Rating</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content with Sidebar Layout */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
        <div className="grid lg:grid-cols-4 gap-6 lg:gap-8">
          {/* Sidebar Filters */}
          <div className="lg:col-span-1 order-2 lg:order-1">
            <div className="bg-white rounded-lg border border-gray-200 p-4 sm:p-6 sticky top-4">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Filter Services</h3>
              
              {/* Search */}
              <div className="mb-6">
                <input
                  type="text"
                  placeholder="Search new services..."
                  className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm sm:text-base"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>

              {/* Service Types */}
              <div className="space-y-2 mb-6">
                <h4 className="text-sm font-medium text-gray-700 mb-3">Service Types</h4>
                {badges.map(badge => (
                  <button
                    key={badge}
                    onClick={() => setFilterBadge(badge)}
                    className={`w-full text-left px-3 sm:px-4 py-2 sm:py-3 rounded-lg transition-colors text-sm sm:text-base ${
                      filterBadge === badge
                        ? 'bg-blue-50 text-blue-700 border border-blue-200'
                        : 'hover:bg-gray-50 text-gray-700'
                    }`}
                  >
                    <div className="flex justify-between items-center">
                      <span>{formatDisplayName(badge)}</span>
                      <span className={`text-xs sm:text-sm ${
                        filterBadge === badge ? 'text-blue-600' : 'text-gray-500'
                      }`}>
                        {badge === 'all' ? NEW_SERVICES.length : 
                         NEW_SERVICES.filter(s => s.badge === badge).length}
                      </span>
                    </div>
                  </button>
                ))}
              </div>

              {/* Sort Options */}
              <div>
                <h4 className="text-sm font-medium text-gray-700 mb-3">Sort By</h4>
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                >
                  <option value="newest">Newest First</option>
                  <option value="rating">Highest Rated</option>
                  <option value="price-low">Price: Low to High</option>
                  <option value="price-high">Price: High to Low</option>
                </select>
              </div>

              {/* Results Summary */}
              <div className="mt-6 pt-4 border-t border-gray-200">
                <div className="text-sm text-gray-600">
                  <div>Showing {sortedServices.length} of {NEW_SERVICES.length} services</div>
                  {(searchTerm || filterBadge !== 'all') && (
                    <button
                      onClick={() => {
                        setSearchTerm('');
                        setFilterBadge('all');
                        setSortBy('newest');
                      }}
                      className="text-blue-600 hover:text-blue-700 font-medium mt-2 text-xs"
                    >
                      Clear all filters
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Services Grid */}
          <div className="lg:col-span-3 order-1 lg:order-2">
            {sortedServices.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4 sm:gap-6">
                {sortedServices.map((service) => (
                  <NewServiceCard
                    key={service.id}
                    service={service}
                    onBookNow={handleBookNow}
                    onLearnMore={handleLearnMore}
                    className="h-full"
                  />
                ))}
              </div>
            ) : (
              <div className="text-center py-12 bg-white rounded-lg shadow-sm border border-gray-200">
                <div className="text-6xl mb-4">🔍</div>
                <h3 className="text-xl font-medium text-gray-900 mb-2">No services found</h3>
                <p className="text-gray-600 mb-4">Try adjusting your search or filter criteria</p>
                <button
                  onClick={() => {
                    setSearchTerm('');
                    setFilterBadge('all');
                    setSortBy('newest');
                  }}
                  className="px-6 py-3 bg-gradient-to-r from-blue-500 to-cyan-500 text-white rounded-lg hover:from-blue-600 hover:to-cyan-600 transition-all duration-200 font-medium"
                >
                  Clear Filters
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Footer */}
      <Footer onNavigate={onNavigate} />

      <QuickBookingModal
        isOpen={showQuickBookingModal}
        bookingData={preBookingData}
        onClose={() => {
          setShowQuickBookingModal(false);
          setPreBookingData(null);
        }}
        onContinue={handlePreBookingContinue}
      />
    </div>
  );
};

export default NewServicePage;

